import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  ScanBarcode,
  Info,
  CheckCircle2,
  AlertCircle,
  X,
  Wand2,
  Zap,
} from "lucide-react";
import { toast } from "sonner";
import PropTypes from "prop-types";

const AddSerialsModal = ({
  isOpen,
  onClose,
  onSave,
  initialSerials = [],
  quantity,
  productName,
  excludeSerials = [], // New prop to exclude already used serials
}) => {
  const [serialsText, setSerialsText] = useState("");
  const [serialsList, setSerialsList] = useState([]);
  const [error, setError] = useState(null);
  const [serialGenConfig, setSerialGenConfig] = useState({
    prefix: "SN",
    start: 1,
    count: quantity >= 999999 ? 1 : quantity,
  });

  const isUnlimited = quantity >= 999999;

  useEffect(() => {
    if (isOpen) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setSerialsText(initialSerials.join("\n"));
      setSerialsList(initialSerials);
      setError(null);
      setSerialGenConfig((prev) => ({
        ...prev,
        count: isUnlimited ? 1 : quantity,
      }));
    }
  }, [isOpen, initialSerials, quantity, isUnlimited]);

  const generateSerials = () => {
    const qtyToGen = parseInt(serialGenConfig.count);
    if (isNaN(qtyToGen) || qtyToGen <= 0) {
      toast.error("Please enter a valid count to generate.");
      return;
    }

    const startNum = parseInt(serialGenConfig.start);
    const prefix = serialGenConfig.prefix || "";

    const generated = [...serialsList];
    let skippedCount = 0;
    let i = 0;
    let currentNum = startNum;

    while (i < qtyToGen) {
      const numStr = currentNum.toString().padStart(4, "0");
      const serial = `${prefix}${numStr}`;

      // Check if serial is already in the list or excluded
      if (generated.includes(serial) || excludeSerials.includes(serial)) {
        skippedCount++;
      } else {
        generated.push(serial);
        i++;
      }
      currentNum++;

      // Safety break to prevent infinite loop
      if (currentNum > startNum + qtyToGen + 1000 + excludeSerials.length)
        break;
    }

    const newText = generated.join("\n");
    setSerialsText(newText);
    setSerialsList(generated);
    setError(null);

    if (skippedCount > 0) {
      toast.success(
        `Generated ${i} serial numbers. Skipped ${skippedCount} existing/reserved serials.`,
      );
    } else {
      toast.success(`Generated ${qtyToGen} serial numbers`);
    }
  };

  const clearSerials = () => {
    setSerialsText("");
    setSerialsList([]);
    setError(null);
    toast.info("All serial numbers cleared");
  };

  const handleTextChange = (e) => {
    const text = e.target.value;
    setSerialsText(text);

    // Parse serials (one per line or comma separated)
    const list = text
      .split(/[\n,]/)
      .map((s) => s.trim())
      .filter((s) => s.length > 0);

    setSerialsList(list);

    // Check for duplicates in the current list
    const hasInternalDuplicates = new Set(list).size !== list.length;
    // Check for duplicates in the excluded list
    const duplicatesInExcluded = list.filter((s) => excludeSerials.includes(s));

    if (hasInternalDuplicates) {
      setError("Duplicate serial numbers detected in your entry.");
    } else if (duplicatesInExcluded.length > 0) {
      setError(
        `Some serial numbers are already in use or reserved: ${duplicatesInExcluded.slice(0, 3).join(", ")}${duplicatesInExcluded.length > 3 ? "..." : ""}`,
      );
    } else if (!isUnlimited && list.length > quantity) {
      // Don't show error if we want qty to be drive by serials,
      // but showing as warning is fine.
      // Actually, user said received qty will be got based on count,
      // so entering more than 'quantity' (which probably is ordered_qty) might be intentional.
      setError(null);
    } else {
      setError(null);
    }
  };

  const handleSave = () => {
    // Check for duplicates in the list
    const uniqueSerials = new Set(serialsList);
    if (uniqueSerials.size !== serialsList.length) {
      toast.error("Duplicate serial numbers detected in your entry.");
      return;
    }

    // Check against excluded serials
    const duplicatesInExcluded = serialsList.filter((s) =>
      excludeSerials.includes(s),
    );
    if (duplicatesInExcluded.length > 0) {
      toast.error(
        `Some serial numbers are already in use: ${duplicatesInExcluded[0]}`,
      );
      return;
    }

    onSave(serialsList);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md p-0 overflow-hidden">
        <DialogHeader className="p-6 bg-primary text-primary-foreground">
          <DialogTitle className="flex items-center gap-2">
            <ScanBarcode className="h-5 w-5" />
            Add Serial Numbers
          </DialogTitle>
          <DialogDescription className="text-primary-foreground/80 text-xs">
            Manage or scan unique serial numbers for this product.
          </DialogDescription>
        </DialogHeader>

        <div className="p-6 space-y-4">
          <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg border border-border">
            <div>
              <p className="text-sm font-medium">{productName}</p>
              {!isUnlimited && (
                <p className="text-xs text-muted-foreground">
                  Required: {quantity} units
                </p>
              )}
            </div>
            <div
              className={`px-2 py-1 rounded text-xs font-bold transition-colors ${
                isUnlimited
                  ? serialsList.length > 0
                    ? "bg-green-600 text-white shadow-sm"
                    : "bg-secondary text-secondary-foreground"
                  : serialsList.length === quantity
                    ? "bg-green-600 text-white shadow-sm"
                    : "bg-secondary text-secondary-foreground"
              }`}
            >
              {isUnlimited ? (
                <span>{serialsList.length} Serials</span>
              ) : (
                <span>
                  {serialsList.length} / {quantity}
                </span>
              )}
            </div>
          </div>

          <Alert className="bg-secondary border-secondary/50">
            <Info className="h-4 w-4 text-secondary-foreground" />
            <AlertDescription className="text-xs text-secondary-foreground font-medium">
              Enter each serial number on a new line or separated by commas. You
              can scan them sequentially.
            </AlertDescription>
          </Alert>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="serials-input">Serial Numbers</Label>
              <div className="flex items-center gap-2">
                {serialsList.length > 0 && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={clearSerials}
                    className="h-7 px-2 text-[10px] text-destructive hover:text-destructive hover:bg-destructive/10 gap-1 font-bold"
                  >
                    <X className="h-3 w-3" />
                    Clear All
                  </Button>
                )}
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-7 px-2 text-[10px] text-primary hover:text-primary/80 hover:bg-primary/10 gap-1 font-bold"
                    >
                      <Wand2 className="h-3 w-3" />
                      Auto-Generate
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-64 p-4">
                    <div className="space-y-3">
                      <div className="flex items-center gap-2 mb-2">
                        <Zap className="h-4 w-4 text-amber-500" />
                        <span className="text-xs font-bold">
                          Sequence Generator
                        </span>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div className="space-y-1">
                          <Label className="text-[10px]">Prefix</Label>
                          <Input
                            className="h-8 text-xs"
                            value={serialGenConfig.prefix}
                            onChange={(e) =>
                              setSerialGenConfig({
                                ...serialGenConfig,
                                prefix: e.target.value,
                              })
                            }
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-[10px]">Start No.</Label>
                          <Input
                            type="number"
                            className="h-8 text-xs"
                            value={serialGenConfig.start}
                            onChange={(e) => {
                              const val = parseInt(e.target.value);
                              setSerialGenConfig({
                                ...serialGenConfig,
                                start: isNaN(val) ? "" : val,
                              });
                            }}
                          />
                        </div>
                        <div className="space-y-1 col-span-2">
                          <Label className="text-[10px]">
                            Count to Generate
                          </Label>
                          <Input
                            type="number"
                            className="h-8 text-xs"
                            value={serialGenConfig.count}
                            onChange={(e) => {
                              const val = parseInt(e.target.value);
                              setSerialGenConfig({
                                ...serialGenConfig,
                                count: isNaN(val) ? "" : val,
                              });
                            }}
                            min={1}
                          />
                        </div>
                      </div>
                      <Button
                        type="button"
                        size="sm"
                        className="w-full text-xs"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          generateSerials();
                        }}
                      >
                        Generate Sequence
                      </Button>
                    </div>
                  </PopoverContent>
                </Popover>
              </div>
            </div>
            <Textarea
              id="serials-input"
              placeholder="Scan or type serial numbers here..."
              className={`min-h-[150px] font-mono whitespace-pre ${
                error ? "border-destructive focus-visible:ring-destructive" : ""
              }`}
              value={serialsText}
              onChange={handleTextChange}
            />
          </div>

          {error && (
            <div className="flex items-center gap-2 text-destructive text-xs font-medium bg-destructive/10 p-2 rounded">
              <AlertCircle className="h-4 w-4" />
              {error}
            </div>
          )}

          {((isUnlimited && serialsList.length > 0) ||
            (!isUnlimited && serialsList.length === quantity)) &&
            !error && (
              <div className="flex items-center gap-2 text-green-600 text-xs font-medium bg-green-50 dark:bg-green-900/10 p-2 rounded">
                <CheckCircle2 className="h-4 w-4" />
                All serial numbers entered correctly.
              </div>
            )}
        </div>

        <DialogFooter className="p-6 pt-0">
          <Button variant="outline" onClick={onClose} type="button">
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={!!error}>
            Save Serials
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

AddSerialsModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  initialSerials: PropTypes.array,
  quantity: PropTypes.number.isRequired,
  productName: PropTypes.string,
};

export default AddSerialsModal;
